import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaVideo,
  FaUsers,
  FaShieldAlt,
  FaCheckCircle,
  FaArrowLeft,
  FaExclamationTriangle,
  FaLock,
  FaEnvelope
} from "react-icons/fa";
import { getApiUrl } from "../../utils/api";
import "./EventBooking.css";

const EventBooking = () => {
  const { slug } = useParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Form State
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);

  // Submission State
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(null);

  // Live seat stats
  const [seatInfo, setSeatInfo] = useState({
    totalSeats: 0,
    bookedSeats: 0,
    remainingSeats: 0,
    isFullyBooked: false
  });

  const fetchEventData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await fetch(getApiUrl(`/events/${slug}`));
      const data = await res.json();

      if (data.success && data.event) {
        setEvent(data.event);
        const total = data.event.totalSeats || 0;
        const booked = data.event.bookedSeats || 0;
        const remaining = Math.max(0, total - booked);
        setSeatInfo({
          totalSeats: total,
          bookedSeats: booked,
          remainingSeats: remaining,
          isFullyBooked: remaining === 0
        });
      } else {
        setLoadError(data.message || "Event not found or not open for registration");
      }
    } catch (err) {
      console.error("❌ Booking Fetch Error:", err);
      setLoadError("Network error loading booking details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Client-side validations
    if (!fullName.trim()) {
      setFormError("Please enter your Full Name");
      return;
    }
    if (!email.trim()) {
      setFormError("Please enter your Email Address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setFormError("Please enter a valid Email Address");
      return;
    }
    if (!phone.trim()) {
      setFormError("Please enter your Phone Number");
      return;
    }
    if (!agreeTerms) {
      setFormError("You must agree to the Terms & Conditions to proceed");
      return;
    }
    if (!agreePrivacy) {
      setFormError("You must agree to the Privacy Policy to proceed");
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(getApiUrl(`/events/${slug}/register`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerName: fullName.trim(),
          customerEmail: email.trim(),
          customerPhone: phone.trim(),
          termsAccepted: agreeTerms,
          privacyAccepted: agreePrivacy
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRegistrationSuccess(data.registration);
      } else {
        // Handle seat full during submission or duplicate
        if (data.isFullyBooked) {
          setSeatInfo((prev) => ({ ...prev, remainingSeats: 0, isFullyBooked: true }));
          setFormError("All seats are filled. Registration is now closed for this event.");
        } else {
          setFormError(data.message || "Registration failed. Please try again.");
        }
      }
    } catch (err) {
      console.error("❌ Registration submission error:", err);
      setFormError("Network error submitting registration. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="event-booking-page-container">
        <div className="booking-loading">
          <div className="events-spinner" />
          <p>Verifying event availability & securing reservation...</p>
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="event-booking-page-container">
        <div className="booking-error-screen">
          <FaExclamationTriangle className="error-icon" />
          <h2>Booking Unavailable</h2>
          <p>{loadError || "This event is currently unavailable for booking."}</p>
          <Link to="/events" className="booking-return-btn">
            <FaArrowLeft /> Return to Events
          </Link>
        </div>
      </div>
    );
  }

  const { totalSeats, bookedSeats, remainingSeats, isFullyBooked } = seatInfo;
  const percentage = totalSeats > 0 ? Math.min(100, Math.round((bookedSeats / totalSeats) * 100)) : 0;

  return (
    <div className="event-booking-page-container">
      {/* Breadcrumb */}
      <div className="booking-nav-row">
        <Link to={`/events/eventdetail/${event.slug}`} className="booking-back-link">
          <FaArrowLeft /> Back to Event Details
        </Link>
      </div>

      <div className="booking-main-wrapper">
        {/* LEFT SECTION: EVENT SUMMARY */}
        <section className="booking-summary-card">
          <div className="summary-banner-box">
            <img
              src={event.bannerImage || event.eventBannerUrl || event.bannerImageUrl}
              alt={event.title}
              className="summary-banner-img"
            />
            <div className="summary-banner-overlay" />
            <div className="summary-banner-badge">{event.category}</div>
          </div>

          <div className="summary-details">
            <h1 className="summary-event-title">{event.title}</h1>
            <p className="summary-event-desc">{event.shortDescription}</p>

            {/* Quick Meta */}
            <div className="summary-meta-grid">
              <div className="summary-meta-row">
                <FaCalendarAlt className="meta-icon" />
                <span><b>Date:</b> {event.eventDate}</span>
              </div>
              <div className="summary-meta-row">
                <FaClock className="meta-icon" />
                <span><b>Time (IST):</b> {event.startTime} - {event.endTime}</span>
              </div>
              <div className="summary-meta-row">
                <FaClock className="meta-icon" />
                <span><b>Duration:</b> {event.durationMinutes} mins</span>
              </div>
              <div className="summary-meta-row">
                <FaVideo className="meta-icon" />
                <span><b>Platform:</b> {event.meetingPlatform || "Google Meet"}</span>
              </div>
            </div>

            {/* Seat Availability Tracker */}
            <div className="summary-seat-tracker">
              <div className="seat-tracker-header">
                <span className="seat-tracker-label">
                  <FaUsers /> Live Seat Availability
                </span>
                <span className={`seat-tracker-remaining ${isFullyBooked ? "full" : ""}`}>
                  {isFullyBooked ? (
                    <b>0 Seats Left</b>
                  ) : (
                    <>
                      <b>{remainingSeats}</b> of {totalSeats} Remaining
                    </>
                  )}
                </span>
              </div>
              <div className="seat-tracker-track">
                <div
                  className={`seat-tracker-fill ${isFullyBooked ? "full" : ""}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="seat-tracker-note">
                {isFullyBooked
                  ? "Sorry! All seats for this event have been booked."
                  : `${percentage}% of available seats have been confirmed.`}
              </span>
            </div>

            <div className="summary-security-note">
              <FaLock className="lock-icon" />
              <span>
                Meeting joining link and calendar instructions will be sent securely to your registered email after confirmation.
              </span>
            </div>
          </div>
        </section>

        {/* RIGHT SECTION: REGISTRATION FORM OR SUCCESS SCREEN */}
        <section className="booking-form-card">
          {registrationSuccess ? (
            /* REGISTRATION SUCCESS CELEBRATION */
            <div className="registration-success-box">
              <div className="success-icon-badge">
                <FaCheckCircle />
              </div>
              <h2 className="success-title">Your Seat is Confirmed! 🎉</h2>
              <p className="success-subtitle">
                Thank you, <b>{registrationSuccess.customerName}</b>. Your booking has been successfully saved.
              </p>

              {/* Confirmation Details Card */}
              <div className="success-meta-card">
                <div className="success-meta-item">
                  <span className="meta-label">Registration ID:</span>
                  <span className="meta-value id-badge">
                    {registrationSuccess.registrationId}
                  </span>
                </div>
                <div className="success-meta-item">
                  <span className="meta-label">Registered Email:</span>
                  <span className="meta-value">{registrationSuccess.customerEmail}</span>
                </div>
                <div className="success-meta-item">
                  <span className="meta-label">Event:</span>
                  <span className="meta-value">{registrationSuccess.eventTitle}</span>
                </div>
                <div className="success-meta-item">
                  <span className="meta-label">Date & Time:</span>
                  <span className="meta-value">
                    {registrationSuccess.eventDate} ({registrationSuccess.startTime} - {registrationSuccess.endTime} IST)
                  </span>
                </div>
                <div className="success-meta-item">
                  <span className="meta-label">Platform:</span>
                  <span className="meta-value highlight">{registrationSuccess.meetingPlatform}</span>
                </div>
              </div>

              {/* Email Sent Notice */}
              <div className="success-email-notice">
                <FaEnvelope className="email-icon" />
                <span>
                  A confirmation email with the private <b>{registrationSuccess.meetingPlatform}</b> joining URL has been dispatched to <b>{registrationSuccess.customerEmail}</b>. Please check your inbox or spam folder.
                </span>
              </div>

              <div className="success-actions-row">
                <Link to={`/events/eventdetail/${slug}`} className="success-return-btn">
                  Back to Event Details
                </Link>
                <Link to="/events" className="success-all-btn">
                  Explore More Events
                </Link>
              </div>
            </div>
          ) : (
            /* REGISTRATION FORM */
            <div className="booking-form-container">
              <div className="booking-form-header">
                <h2>Reserve Your Seat</h2>
                <p>Complete your information below to register for this live session.</p>
              </div>

              {formError && (
                <div className="booking-error-banner">
                  <FaExclamationTriangle />
                  <span>{formError}</span>
                </div>
              )}

              {isFullyBooked ? (
                <div className="booking-closed-banner">
                  <h3>All Seats Filled - Registration Closed</h3>
                  <p>Sorry! All seats for this event have been booked.</p>
                  <Link to={`/events/eventdetail/${slug}`} className="back-to-detail-btn">
                    Return to Event Details
                  </Link>
                </div>
              ) : (
                <form className="booking-registration-form" onSubmit={handleSubmit}>
                  {/* Full Name */}
                  <div className="form-group">
                    <label htmlFor="fullName" className="form-label">
                      Full Name <span className="required-star">*</span>
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      className="form-input"
                      placeholder="e.g. Alex Johnson"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={submitting}
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required-star">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="form-input"
                      placeholder="e.g. alex@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submitting}
                      required
                    />
                    <span className="input-hint">The joining link will be sent to this email address.</span>
                  </div>

                  {/* Phone */}
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number <span className="required-star">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      className="form-input"
                      placeholder="e.g. +91 9876543210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={submitting}
                      required
                    />
                  </div>

                  {/* Mandatory Checkboxes */}
                  <div className="form-checkbox-group">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        disabled={submitting}
                        required
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-text">
                        I agree to the{" "}
                        <Link
                          to="/terms-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="policy-link"
                        >
                          Terms & Conditions
                        </Link>
                      </span>
                    </label>

                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={agreePrivacy}
                        onChange={(e) => setAgreePrivacy(e.target.checked)}
                        disabled={submitting}
                        required
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-text">
                        I agree to the{" "}
                        <Link
                          to="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="policy-link"
                        >
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="booking-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <span className="btn-loading-flex">
                        <span className="button-spinner" /> Reserving Seat...
                      </span>
                    ) : (
                      "Confirm & Book Seat"
                    )}
                  </button>

                  <div className="form-trust-footer">
                    <FaShieldAlt className="shield-icon" />
                    <span>Your privacy is protected. We will never share or sell your details.</span>
                  </div>
                </form>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default EventBooking;
